import { lazy, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Route, Routes } from "react-router";
import "./i18n";
import Home from "./component/page/home";
import Login from "./component/page/login";
import TeamInvite from "./component/page/teamInvite";
import NotFound from "./component/page/NotFound";
import SongPlatformsProvider from "./component/library/SongPlatformsProvider";
import DiscussionQuestionsProvider from "./component/service/DiscussionQuestionsProvider";
import PageTransition from "./component/navigation/PageTransition";
import {
  CHURCH_ROUTE,
  EVENT_DETAIL_ROUTE,
  LAUNCHPAD_ROUTE,
  LAUNCHPAD_TAG_ROUTE,
  PLAYLIST_DETAIL_ROUTE,
  SERVICE_DETAIL_ROUTE,
  TEST_ROUTE,
  TEST_SAMPLE_ROUTE,
} from "./routes";

// Launchpads and the pages inside them, in their own chunk.
type LaunchpadPages = typeof import("./launchpadPages");

/** Set once the chunk has loaded, so later pages from it render straight away. */
let launchpadPages: LaunchpadPages | null = null;

const loadLaunchpadPages = () =>
  import("./launchpadPages").then((pages) => (launchpadPages = pages));

// Each page gets its own lazy() only for the first page opened from the
// chunk. React.lazy suspends on its first render even when the module is
// already loaded — it only learns that asynchronously — and a navigation that
// suspends keeps the old page up, then swaps the new one in on its own a
// moment later. Opening an event from a Launchpad would do exactly that the
// first time, and not slide in like every later time. Rendering the loaded
// page directly keeps the navigation in one render, like any other.
function launchpadPage(name: keyof LaunchpadPages) {
  const Lazy = lazy(() => loadLaunchpadPages().then((pages) => ({ default: pages[name] })));

  return function LaunchpadPage() {
    const Page = launchpadPages?.[name];
    return Page ? <Page /> : <Lazy />;
  };
}

const LaunchpadByID = launchpadPage("LaunchpadByID");
const LaunchpadByTag = launchpadPage("LaunchpadByTag");
const ServiceDetail = launchpadPage("ServiceDetail");
const EventDetail = launchpadPage("EventDetail");
const PlaylistDetail = launchpadPage("PlaylistDetail");
const ChurchLaunchpads = launchpadPage("ChurchLaunchpads");

// Dev tools — the Launchpad picker, the bundled sample and the component
// gallery — exist only in `npm start`. Staging and production builds leave
// them out, along with the sample data.
//
// Parcel inlines NODE_ENV at build time, then:
// - drops the import() below before resolving imports, so the pages and sample
//   data aren't bundled or deployed at all. It only does that when the check is
//   written inline around the import(), not through a variable, and a static
//   import would be kept because the sample data runs code when it loads.
// - lets the minifier fold SHOW_DEV_PAGES to `false`, removing the routes.
// Hence the same condition twice; keep them in step.
const SHOW_DEV_PAGES = process.env.NODE_ENV !== "production";

const loadDevPages = process.env.NODE_ENV !== "production"
  ? () => import("./devPages")
  : null;

function devPage(name: keyof Awaited<ReturnType<NonNullable<typeof loadDevPages>>>) {
  return lazy(() => loadDevPages!().then((pages) => ({ default: pages[name] })));
}

const Test = devPage("Test");
const SampleLaunchpad = devPage("SampleLaunchpad");
const Library = devPage("Library");

const container = document.getElementById("app");
if (!container) throw new Error("Missing #app container");

createRoot(container).render(
  <BrowserRouter>
    {/* Sheets presented from anywhere, standing in for the app's Navigator.
        Outside PageTransition so a sheet isn't caught in the incoming page's
        transform. */}
    <SongPlatformsProvider>
      <DiscussionQuestionsProvider>
        <PageTransition>
          <Routes>
            <Route path="/" element={<Home />} />

            {/* Universal link targets, claimed in scripts/build-aasa.mjs */}
            <Route path="/app/login/:code" element={<Login />} />
            <Route path="/app/church/:churchId/team/:teamId" element={<TeamInvite />} />

            <Route path={LAUNCHPAD_TAG_ROUTE} element={<Suspense><LaunchpadByTag /></Suspense>} />
            <Route path={CHURCH_ROUTE} element={<Suspense><ChurchLaunchpads /></Suspense>} />
            <Route path={LAUNCHPAD_ROUTE} element={<Suspense><LaunchpadByID /></Suspense>} />
            {/* Also a universal link target: the app shares service links. */}
            <Route path={SERVICE_DETAIL_ROUTE} element={<Suspense><ServiceDetail /></Suspense>} />
            <Route path={EVENT_DETAIL_ROUTE} element={<Suspense><EventDetail /></Suspense>} />
            <Route path={PLAYLIST_DETAIL_ROUTE} element={<Suspense><PlaylistDetail /></Suspense>} />

            {SHOW_DEV_PAGES && <>
              <Route path={TEST_ROUTE} element={<Suspense><Test /></Suspense>} />
              <Route path={TEST_SAMPLE_ROUTE} element={<Suspense><SampleLaunchpad /></Suspense>} />
              <Route path="/library" element={<Suspense><Library /></Suspense>} />
            </>}

            <Route path="*" element={<NotFound />} />
          </Routes>
        </PageTransition>
      </DiscussionQuestionsProvider>
    </SongPlatformsProvider>
  </BrowserRouter>
);
